import React, { useState } from 'react';

// Mock data for assignments
const mockAssignments = [
  { id: 1, title: 'Essay on Climate Change', dueDate: '2025-04-15', status: 'In Progress', course: 'Environmental Science 101' },
  { id: 2, title: 'Programming Project: Data Analysis', dueDate: '2025-04-22', status: 'Not Started', course: 'Computer Science 202' },
  { id: 3, title: 'Research Report: Renewable Energy', dueDate: '2025-05-01', status: 'Submitted', course: 'Environmental Science 101' },
];

// Mock data for feedback threads
const mockFeedbackThreads = [
  { 
    id: 1, 
    assignmentId: 3, 
    messages: [
      { sender: 'ai', content: 'Your research report shows strong analysis of current renewable energy trends. The section on solar energy implementation is particularly well-researched.', timestamp: '2025-04-21T10:30:00' },
      { sender: 'student', content: 'Thank you! I was wondering if I should expand more on wind energy potential?', timestamp: '2025-04-21T10:35:00' },
      { sender: 'ai', content: 'That would strengthen your report. Consider adding data on recent wind farm efficiency improvements and their economic impact.', timestamp: '2025-04-21T10:36:00' },
    ]
  },
  { 
    id: 2, 
    assignmentId: 1, 
    messages: [
      { sender: 'ai', content: 'Your draft has a clear thesis on climate change impacts. To improve, consider adding more specific examples to support your arguments in paragraphs 2 and 4.', timestamp: '2025-04-14T15:20:00' },
      { sender: 'student', content: 'Could you suggest some specific examples I could include?', timestamp: '2025-04-14T15:25:00' },
      { sender: 'ai', content: 'For paragraph 2, you could include recent flooding events in Southeast Asia as concrete examples of increasing extreme weather. For paragraph 4, consider citing the economic impact studies from the latest IPCC report.', timestamp: '2025-04-14T15:27:00' },
    ]
  }
];

const StudentDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('assignments');
  const [selectedAssignment, setSelectedAssignment] = useState<number | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [activeFeedbackThread, setActiveFeedbackThread] = useState<number | null>(null);
  const [showSubmissionForm, setShowSubmissionForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preferredLanguage, setPreferredLanguage] = useState('English');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would handle the actual submission
    alert(`File "${selectedFile?.name}" submitted successfully!`);
    setShowSubmissionForm(false);
    setSelectedFile(null);
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (newMessage.trim() === '') return;
    
    // In a real app, you would send this to your backend
    console.log('Sending message:', newMessage);
    setNewMessage('');
    
    // For demo purposes, we'll just add it to our mock data
    if (activeFeedbackThread !== null) {
      const threadIndex = mockFeedbackThreads.findIndex(thread => thread.id === activeFeedbackThread);
      if (threadIndex !== -1) {
        mockFeedbackThreads[threadIndex].messages.push({
          sender: 'student',
          content: newMessage,
          timestamp: new Date().toISOString()
        });
        
        // Simulate AI response
        setTimeout(() => {
          mockFeedbackThreads[threadIndex].messages.push({
            sender: 'ai',
            content: 'Thank you for your message. I\'ll analyze this and provide feedback shortly.',
            timestamp: new Date().toISOString()
          });
        }, 1000);
      }
    }
  };

  return (
    <div className="bg-neutral min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-primary">Student Dashboard</h1>
            <p className="text-gray-600 mt-2">Manage your assignments and feedback</p>
          </div>
          <div className="mt-4 md:mt-0">
            <button 
              onClick={() => setShowSubmissionForm(!showSubmissionForm)}
              className="btn btn-primary"
            >
              Submit New Assignment
            </button>
          </div>
        </div>

        {/* Submission Form */}
        {showSubmissionForm && (
          <div className="card mb-8">
            <h2 className="text-xl font-bold text-primary mb-4">Submit Assignment</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label htmlFor="assignmentSelect" className="block text-sm font-medium text-gray-700 mb-1">
                  Select Assignment
                </label>
                <select
                  id="assignmentSelect"
                  className="input w-full"
                  required
                >
                  <option value="">-- Select an assignment --</option>
                  {mockAssignments
                    .filter(a => a.status !== 'Submitted')
                    .map(assignment => (
                      <option key={assignment.id} value={assignment.id}>
                        {assignment.title} - {assignment.course}
                      </option>
                    ))}
                </select>
              </div>
              
              <div className="mb-6">
                <label htmlFor="fileUpload" className="block text-sm font-medium text-gray-700 mb-1">
                  Upload File
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none"
                      >
                        <span>Upload a file</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          onChange={handleFileChange}
                          required
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PDF, DOCX, TXT, PY, JAVA up to 10MB
                    </p>
                    {selectedFile && (
                      <p className="text-sm text-primary mt-2">
                        Selected: {selectedFile.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-1">
                  Preferred Feedback Language
                </label>
                <select
                  id="language"
                  className="input w-full"
                  value={preferredLanguage}
                  onChange={(e) => setPreferredLanguage(e.target.value)}
                >
                  <option value="English">English</option>
                  <option value="Spanish">Spanish</option>
                  <option value="French">French</option>
                  <option value="German">German</option>
                  <option value="Chinese">Chinese</option>
                  <option value="Japanese">Japanese</option>
                  <option value="Arabic">Arabic</option>
                </select>
              </div>
              
              <div className="mb-6">
                <label htmlFor="comments" className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Comments (Optional)
                </label>
                <textarea
                  id="comments"
                  rows={3}
                  className="input w-full"
                  placeholder="Any specific areas you'd like feedback on?"
                />
              </div>
              
              <div className="flex justify-end">
                <button 
                  type="button" 
                  className="btn bg-gray-200 text-gray-800 hover:bg-gray-300 mr-4"
                  onClick={() => setShowSubmissionForm(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={!selectedFile}
                >
                  Submit Assignment
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Dashboard Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => {
                setActiveTab('assignments');
                setSelectedAssignment(null);
                setActiveFeedbackThread(null);
              }}
              className={`${
                activeTab === 'assignments'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Assignments
            </button>
            <button
              onClick={() => {
                setActiveTab('feedback');
                setSelectedAssignment(null);
              }}
              className={`${
                activeTab === 'feedback'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Feedback
            </button>
            <button
              onClick={() => {
                setActiveTab('profile');
                setSelectedAssignment(null);
                setActiveFeedbackThread(null);
              }}
              className={`${
                activeTab === 'profile'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Profile & Settings
            </button>
          </nav>
        </div>

        {/* Assignments Tab */}
        {activeTab === 'assignments' && !selectedAssignment && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assignment
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Course
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mockAssignments.map((assignment) => (
                  <tr key={assignment.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{assignment.title}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{assignment.course}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{assignment.dueDate}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${assignment.status === 'Submitted' ? 'bg-green-100 text-green-800' : 
                          assignment.status === 'In Progress' ? 'bg-blue-100 text-blue-800' : 
                          'bg-yellow-100 text-yellow-800'}`}>
                        {assignment.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {assignment.status === 'Submitted' ? (
                        <button 
                          onClick={() => {
                            setSelectedAssignment(assignment.id);
                            const thread = mockFeedbackThreads.find(t => t.assignmentId === assignment.id);
                            if (thread) setActiveFeedbackThread(thread.id);
                          }}
                          className="text-primary hover:text-opacity-80"
                        >
                          View Feedback
                        </button>
                      ) : (
                        <button 
                          onClick={() => {
                            setShowSubmissionForm(true);
                          }}
                          className="text-primary hover:text-opacity-80"
                        >
                          Submit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Assignment Feedback View */}
        {activeTab === 'assignments' && selectedAssignment && activeFeedbackThread && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-medium text-primary">
                  {mockAssignments.find(a => a.id === selectedAssignment)?.title}
                </h2>
                <p className="text-sm text-gray-500">
                  {mockAssignments.find(a => a.id === selectedAssignment)?.course}
                </p>
              </div>
              <button 
                onClick={() => {
                  setSelectedAssignment(null);
                  setActiveFeedbackThread(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                Back to Assignments
              </button>
            </div>
            
            <div className="p-4 h-96 overflow-y-auto">
              {mockFeedbackThreads
                .find(thread => thread.id === activeFeedbackThread)
                ?.messages.map((message, index) => (
                  <div 
                    key={index} 
                    className={`flex ${message.sender === 'student' ? 'justify-end' : 'justify-start'} mb-4`}
                  >
                    {message.sender === 'ai' && (
                      <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center text-white font-bold mr-2">
                        AI
                      </div>
                    )}
                    <div 
                      className={`rounded-lg p-3 max-w-xs lg:max-w-md ${
                        message.sender === 'student' 
                          ? 'bg-primary bg-opacity-10 text-gray-800' 
                          : 'bg-gray-100'
                      }`}
                    >
                      <p className="text-sm">{message.content}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {message.sender === 'student' && (
                      <div className="h-10 w-10 rounded-full bg-accent-2 flex items-center justify-center text-white font-bold ml-2">
                        S
                      </div>
                    )}
                  </div>
                ))}
            </div>
            
            <div className="p-4 border-t border-gray-200">
              <form onSubmit={handleSendMessage} className="flex">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  className="input flex-grow mr-2"
                  placeholder="Ask a question about your feedback..."
                />
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={newMessage.trim() === ''}
                >
                  Send
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Feedback Tab */}
        {activeTab === 'feedback' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-primary">Feedback Conversations</h2>
              <p className="text-sm text-gray-500">
                Review and continue your feedback conversations
              </p>
            </div>
            
            <div className="divide-y divide-gray-200">
              {mockFeedbackThreads.map(thread => {
                const assignment = mockAssignments.find(a => a.id === thread.assignmentId);
                const lastMessage = thread.messages[thread.messages.length - 1];
                
                return (
                  <div 
                    key={thread.id} 
                    className="p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      setActiveTab('assignments');
                      setSelectedAssignment(thread.assignmentId);
                      setActiveFeedbackThread(thread.id);
                    }}
                  >
                    <div className="flex justify-between">
                      <h3 className="text-md font-medium text-gray-900">{assignment?.title}</h3>
                      <span className="text-sm text-gray-500">
                        {new Date(lastMessage.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{assignment?.course}</p>
                    <p className="text-sm text-gray-700 mt-2 truncate">
                      <span className="font-medium">{lastMessage.sender === 'ai' ? 'AI: ' : 'You: '}</span>
                      {lastMessage.content}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Profile & Settings Tab */}
        {activeTab === 'profile' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-6">
              <h2 className="text-xl font-bold text-primary mb-6">Profile & Settings</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-1">
                  <div className="flex flex-col items-center">
                    <div className="h-24 w-24 rounded-full bg-primary flex items-center justify-center text-white text-2xl font-bold">
                      JS
                    </div>
                    <h3 className="mt-4 text-lg font-medium text-gray-900">John Smith</h3>
                    <p className="text-gray-500">Student ID: 12345678</p>
                  </div>
                </div>
                
                <div className="md:col-span-2">
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-primary mb-4">Account Settings</h3>
                    
                    <div className="grid grid-cols-1 gap-6">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="email"
                          className="input w-full"
                          defaultValue="john.smith@example.com"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="preferredLanguage" className="block text-sm font-medium text-gray-700 mb-1">
                          Preferred Feedback Language
                        </label>
                        <select
                          id="preferredLanguage"
                          className="input w-full"
                          value={preferredLanguage}
                          onChange={(e) => setPreferredLanguage(e.target.value)}
                        >
                          <option value="English">English</option>
                          <option value="Spanish">Spanish</option>
                          <option value="French">French</option>
                          <option value="German">German</option>
                          <option value="Chinese">Chinese</option>
                          <option value="Japanese">Japanese</option>
                          <option value="Arabic">Arabic</option>
                        </select>
                      </div>
                      
                      <div>
                        <label htmlFor="notifications" className="block text-sm font-medium text-gray-700 mb-1">
                          Notification Preferences
                        </label>
                        <div className="mt-2 space-y-2">
                          <div className="flex items-center">
                            <input
                              id="email-notifications"
                              name="notifications"
                              type="checkbox"
                              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                              defaultChecked
                            />
                            <label htmlFor="email-notifications" className="ml-2 text-sm text-gray-700">
                              Email notifications for new feedback
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              id="due-date-reminders"
                              name="notifications"
                              type="checkbox"
                              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                              defaultChecked
                            />
                            <label htmlFor="due-date-reminders" className="ml-2 text-sm text-gray-700">
                              Due date reminders
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-medium text-primary mb-4">Privacy Settings</h3>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700">Data Usage for AI Improvement</h4>
                          <p className="text-xs text-gray-500">Allow your anonymized submissions to improve AI feedback</p>
                        </div>
                        <div className="relative inline-block w-10 mr-2 align-middle select-none">
                          <input
                            type="checkbox"
                            id="data-usage"
                            className="sr-only"
                            defaultChecked
                          />
                          <label
                            htmlFor="data-usage"
                            className="block h-6 rounded-full cursor-pointer bg-gray-300"
                          >
                            <span className="block h-6 w-6 rounded-full bg-white shadow transform transition-transform duration-200 ease-in-out"></span>
                          </label>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700">Share Progress with Instructors</h4>
                          <p className="text-xs text-gray-500">Allow instructors to see your feedback interactions</p>
                        </div>
                        <div className="relative inline-block w-10 mr-2 align-middle select-none">
                          <input
                            type="checkbox"
                            id="share-progress"
                            className="sr-only"
                            defaultChecked
                          />
                          <label
                            htmlFor="share-progress"
                            className="block h-6 rounded-full cursor-pointer bg-gray-300"
                          >
                            <span className="block h-6 w-6 rounded-full bg-white shadow transform transition-transform duration-200 ease-in-out"></span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 flex justify-end">
                    <button className="btn btn-primary">
                      Save Settings
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentDashboard;
